export interface ParsedPassenger {
  lastname: string;
  firstnames: string;
  registrationNumbers: string;
  phone: string;
  phones: string[];
  address: string;
  subscription: string;
  count: number;
}

export interface SaaSSource {
  id: string;
  name: string;
  parse(sheetData: any[][]): ParsedPassenger[];
}

/**
 * Normalize Moroccan phone numbers:
 *  +212 661-326322 → 0661326322
 *  0661326322 → 0661326322
 *  661326322 → 0661326322
 */
function normalizePhone(raw: string): string {
  if (!raw) return '';
  let phone = raw.replace(/[\s\-\.]/g, '');
  if (phone.startsWith('+212')) {
    phone = '0' + phone.substring(4);
  } else if (phone.startsWith('212') && phone.length >= 12) {
    phone = '0' + phone.substring(3);
  } else if (!phone.startsWith('0') && phone.length === 9) {
    phone = '0' + phone;
  }
  return phone;
}

/**
 * Normalize and deduplicate a list of phone numbers, dropping empty values.
 */
function uniqueNonEmptyPhones(list: string[]): string[] {
  const seen: { [phone: string]: boolean } = {};
  const out: string[] = [];
  for (const raw of list) {
    const n = normalizePhone(raw || '');
    if (!n || seen[n]) continue;
    seen[n] = true;
    out.push(n);
  }
  return out;
}

/**
 * Canonical grouping key for a set of phones (order-independent).
 */
function canonicalPhonesKey(phones: string[]): string {
  return (phones || []).slice().sort().join('|');
}

// ============================================================
// Koolskools Parser
// ============================================================

function parseKoolskools(sheetData: any[][]): ParsedPassenger[] {
  // Find header row by looking for "N.O" in col 0
  let headerRowIndex = -1;
  for (let i = 0; i < Math.min(sheetData.length, 30); i++) {
    const row = sheetData[i];
    if (row && row[0] && row[0].toString().trim() === 'N.O') {
      headerRowIndex = i;
      break;
    }
  }
  if (headerRowIndex === -1) {
    throw new Error('Format Koolskools non reconnu : en-tête "N.O" introuvable.');
  }

  const headers: string[] = sheetData[headerRowIndex].map((h: any) => (h || '').toString().trim());

  // Map column indexes
  const colIdx = {
    nom: headers.indexOf('Nom'),
    prenom: headers.indexOf('Prenom'),
    matricule: headers.indexOf('Matricule'),
    transport: headers.indexOf('Transport'),
    telParent1: headers.indexOf('Tel parent 1'),
    telParent2: headers.indexOf('Tel parent 2'),
    adresse: headers.indexOf('Adresse'),
  };

  if (colIdx.nom === -1 || colIdx.prenom === -1) {
    throw new Error('Colonnes "Nom" et "Prenom" introuvables dans le fichier Koolskools.');
  }

  // Aggregate passengers: group children by lastname + phone (parent)
  // Track seen children by firstname+matricule to avoid duplicates across files
  const passengerMap: { [key: string]: ParsedPassenger & { seenChildren: Set<string> } } = {};

  for (let i = headerRowIndex + 1; i < sheetData.length; i++) {
    const row = sheetData[i];
    if (!row || !row[colIdx.nom]) continue;

    // Filter: only Transport = "Oui"
    if (colIdx.transport !== -1) {
      const transport = (row[colIdx.transport] || '').toString().trim();
      if (transport !== 'Oui') continue;
    }

    const nom = (row[colIdx.nom] || '').toString().trim();
    const prenom = (row[colIdx.prenom] || '').toString().trim();
    const matricule = colIdx.matricule !== -1 ? (row[colIdx.matricule] || '').toString().trim() : '-';
    const tel1 = colIdx.telParent1 !== -1 ? normalizePhone((row[colIdx.telParent1] || '').toString()) : '';
    const tel2 = colIdx.telParent2 !== -1 ? normalizePhone((row[colIdx.telParent2] || '').toString()) : '';
    const phones = uniqueNonEmptyPhones([tel1, tel2]);
    const phone = phones[0] || '';
    const adresse = colIdx.adresse !== -1 ? (row[colIdx.adresse] || '-').toString().trim() : '-';

    const key = nom.toUpperCase() + '_' + canonicalPhonesKey(phones);
    const childKey = (prenom || '').toUpperCase().trim() + '|' + (matricule || '-');

    if (passengerMap[key]) {
      // Skip duplicate child across files
      if (passengerMap[key].seenChildren.has(childKey)) continue;
      passengerMap[key].seenChildren.add(childKey);
      passengerMap[key].firstnames += '/' + prenom.toUpperCase();
      passengerMap[key].registrationNumbers += '/' + matricule.toUpperCase();
      passengerMap[key].count++;
    } else {
      passengerMap[key] = {
        lastname: nom.toUpperCase(),
        firstnames: prenom.toUpperCase(),
        registrationNumbers: matricule.toUpperCase(),
        phone: phone,
        phones: phones,
        address: adresse || '-',
        subscription: 'Aller/Retour',
        count: 1,
        seenChildren: new Set<string>([childKey])
      };
    }
  }

  return Object.values(passengerMap).map(({ seenChildren, ...p }) => p);
}

// ============================================================
// Emadariss Parser
// ============================================================

const EMADARISS_PHONE_PLACEHOLDER = '0600000000';
const COMPOUND_LASTNAME_PREFIXES = ['EL', 'AIT', 'BEN', 'OU', 'OULD'];

/**
 * Split a Moroccan full name "NOM PRENOM" into lastname + firstnames.
 * Handles compound lastnames (EL, AIT, BEN, OU, OULD) and compound firstnames.
 * Examples:
 *   "ABIA YAHYA"                 → lastname "ABIA", firstnames "YAHYA"
 *   "EL WAFDI SALMA"             → lastname "EL WAFDI", firstnames "SALMA"
 *   "KNANOUA FATIMA ZAHRA"       → lastname "KNANOUA", firstnames "FATIMA ZAHRA"
 *   "EL OUADGHIRI MOHAMED IYAD"  → lastname "EL OUADGHIRI", firstnames "MOHAMED IYAD"
 */
function splitEmadarissName(rawName: string): { lastname: string; firstnames: string } {
  const words = (rawName || '').trim().split(/\s+/).filter(w => w.length > 0);
  if (words.length === 0) {
    return { lastname: '-', firstnames: '-' };
  }
  if (words.length === 1) {
    return { lastname: words[0].toUpperCase(), firstnames: '-' };
  }
  if (COMPOUND_LASTNAME_PREFIXES.indexOf(words[0].toUpperCase()) !== -1) {
    return {
      lastname: (words[0] + ' ' + words[1]).toUpperCase(),
      firstnames: words.slice(2).join(' ')
    };
  }
  return {
    lastname: words[0].toUpperCase(),
    firstnames: words.slice(1).join(' ')
  };
}

/**
 * Derive subscription from AM/PM Aller/Retour flags.
 * Flag cells contain '*' when the student is transported.
 */
function deriveEmadarissSubscription(amAller: boolean, amRetour: boolean, pmAller: boolean, pmRetour: boolean): string {
  const hasAller = amAller || pmAller;
  const hasRetour = amRetour || pmRetour;
  if (hasAller && hasRetour) return 'Aller/Retour';
  if (hasAller) return 'Aller';
  if (hasRetour) return 'Retour';
  return 'Aller/Retour';
}

function parseEmadariss(sheetData: any[][]): ParsedPassenger[] {
  const passengerMap: { [key: string]: ParsedPassenger & { seenChildren: Set<string> } } = {};
  let inBlock = false;

  for (let i = 0; i < sheetData.length; i++) {
    const row = sheetData[i];
    if (!row) continue;

    // A new page/block starts at "Planning de transport" (col 18)
    if (row[18] && row[18].toString().trim() === 'Planning de transport') {
      inBlock = true;
      continue;
    }
    if (!inBlock) continue;

    // Stop parsing students of this block once we hit the footer
    const col1 = row[1] ? row[1].toString().trim() : '';
    if (col1.indexOf('Nombre des élèves') !== -1) {
      inBlock = false;
      continue;
    }

    const matriculeRaw = row[2];
    const nameRaw = row[9];
    // Student row: non-empty matricule AND non-empty name.
    // Matricule may be a number (608), a numeric string ("608") or
    // an alphanumeric string ("0238_2024") — keep it as a string.
    const matricule = (matriculeRaw === null || matriculeRaw === undefined)
      ? ''
      : matriculeRaw.toString().trim();
    // Normalize pure float numbers like "608.0" → "608"
    const normalizedMatricule = /^\d+\.0$/.test(matricule) ? matricule.slice(0, -2) : matricule;

    if (!normalizedMatricule || !nameRaw || !nameRaw.toString().trim()) continue;
    const { lastname, firstnames } = splitEmadarissName(nameRaw.toString());
    const address = (row[51] || '-').toString().trim() || '-';

    const amAller = row[29] === '*';
    const amRetour = row[33] === '*';
    const pmAller = row[38] === '*';
    const pmRetour = row[44] === '*';
    const subscription = deriveEmadarissSubscription(amAller, amRetour, pmAller, pmRetour);

    const key = lastname.toUpperCase();
    const childKey = firstnames.toUpperCase().trim() + '|' + normalizedMatricule;

    if (passengerMap[key]) {
      if (passengerMap[key].seenChildren.has(childKey)) continue;
      passengerMap[key].seenChildren.add(childKey);
      passengerMap[key].firstnames += '/' + firstnames;
      passengerMap[key].registrationNumbers += '/' + normalizedMatricule;
      passengerMap[key].count++;
    } else {
      passengerMap[key] = {
        lastname: lastname,
        firstnames: firstnames,
        registrationNumbers: normalizedMatricule,
        phone: EMADARISS_PHONE_PLACEHOLDER,
        phones: [],
        address: address,
        subscription: subscription,
        count: 1,
        seenChildren: new Set<string>([childKey])
      };
    }
  }

  return Object.values(passengerMap).map(({ seenChildren, ...p }) => p);
}

// ============================================================
// Emadariss "avec matricule" Parser
// ============================================================

/**
 * Extract all phone numbers from the "Tél." column which looks like:
 *   "|| P:0666511193"
 *   "D:0537643293| B:0661095761|"
 *   "D:| B:| P:0661148426"
 * Prefixes: P = portable (mobile), D = domicile, B = bureau.
 * Returns them ordered P, B, D then any other prefixes.
 */
function extractEmadarissAvecMatriculePhones(tel: string): string[] {
  if (!tel) return [];
  const parts = tel.split('|').map(s => s.trim()).filter(Boolean);
  const byPrefix: { [prefix: string]: string } = {};
  const fallback: string[] = [];
  for (const part of parts) {
    const m = part.match(/^([A-Za-z]+):(.*)$/);
    if (m) {
      // Keep only the leading phone token (drop trailing words like "ONC")
      const phoneToken = m[2].trim().split(/\s+/)[0] || '';
      const p = normalizePhone(phoneToken);
      if (!p) continue;
      const prefix = m[1].toUpperCase();
      if (!byPrefix[prefix]) byPrefix[prefix] = p;
    } else {
      const p = normalizePhone(part.split(/\s+/)[0] || '');
      if (p) fallback.push(p);
    }
  }
  const order = ['P', 'B', 'D'];
  const result: string[] = [];
  for (const pfx of order) {
    if (byPrefix[pfx]) result.push(byPrefix[pfx]);
  }
  for (const pfx in byPrefix) {
    if (order.indexOf(pfx) === -1) result.push(byPrefix[pfx]);
  }
  return result.concat(fallback);
}

function parseEmadarissAvecMatricule(sheetData: any[][]): ParsedPassenger[] {
  const passengerMap: { [key: string]: ParsedPassenger & { seenChildren: Set<string> } } = {};
  let inBlock = false;

  for (let i = 0; i < sheetData.length; i++) {
    const row = sheetData[i];
    if (!row) continue;

    // A new page/block starts at the "Matricule" header (col 2)
    if (row[2] && row[2].toString().trim() === 'Matricule') {
      inBlock = true;
      continue;
    }
    if (!inBlock) continue;

    // Stop parsing students of this block once we hit the footer
    const col1 = row[1] ? row[1].toString().trim() : '';
    if (col1.indexOf('Nombre des élèves') !== -1) {
      inBlock = false;
      continue;
    }

    const matriculeRaw = row[2];
    const nameRaw = row[11];
    const matricule = (matriculeRaw === null || matriculeRaw === undefined)
      ? ''
      : matriculeRaw.toString().trim();
    // Normalize pure float numbers like "608.0" → "608"
    const normalizedMatricule = /^\d+\.0$/.test(matricule) ? matricule.slice(0, -2) : matricule;

    if (!normalizedMatricule || !nameRaw || !nameRaw.toString().trim()) continue;

    const { lastname, firstnames } = splitEmadarissName(nameRaw.toString());
    const smsPhone = normalizePhone((row[23] || '').toString().trim());
    const otherPhones = extractEmadarissAvecMatriculePhones((row[27] || '').toString().trim());
    const phones = uniqueNonEmptyPhones([smsPhone, ...otherPhones]);
    const phone = phones[0] || '';

    const key = lastname.toUpperCase() + '_' + canonicalPhonesKey(phones);
    const childKey = firstnames.toUpperCase().trim() + '|' + normalizedMatricule;

    if (passengerMap[key]) {
      if (passengerMap[key].seenChildren.has(childKey)) continue;
      passengerMap[key].seenChildren.add(childKey);
      passengerMap[key].firstnames += '/' + firstnames;
      passengerMap[key].registrationNumbers += '/' + normalizedMatricule;
      passengerMap[key].count++;
    } else {
      passengerMap[key] = {
        lastname: lastname,
        firstnames: firstnames,
        registrationNumbers: normalizedMatricule,
        phone: phone,
        phones: phones,
        address: '-',
        subscription: 'Aller/Retour',
        count: 1,
        seenChildren: new Set<string>([childKey])
      };
    }
  }

  return Object.values(passengerMap).map(({ seenChildren, ...p }) => p);
}

// ============================================================
// Grouping helpers
// ============================================================

function mergeUniqueTokens(a: string, b: string): string {
  const seen: { [t: string]: boolean } = {};
  const out: string[] = [];
  for (const raw of [a, b]) {
    if (!raw) continue;
    for (const tok of raw.split('/')) {
      const t = tok.trim();
      if (!t || t === '-') continue;
      const key = t.toUpperCase();
      if (seen[key]) continue;
      seen[key] = true;
      out.push(t);
    }
  }
  return out.join('/');
}

/**
 * Group passengers that share the same lastname AND at least one phone number.
 * Handles swapped parent1/parent2 phones and partial phone overlaps across files.
 */
export function groupPassengersBySharedPhone(passengers: ParsedPassenger[]): ParsedPassenger[] {
  const n = passengers.length;
  if (n === 0) return [];

  const parent: number[] = [];
  for (let i = 0; i < n; i++) parent.push(i);
  const find = (x: number): number => {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]];
      x = parent[x];
    }
    return x;
  };
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  // phone -> lastname -> passenger indexes
  const buckets: { [phone: string]: { [lastname: string]: number[] } } = {};
  for (let i = 0; i < n; i++) {
    const p = passengers[i];
    const lastname = (p.lastname || '').toUpperCase().trim();
    const phones = (p.phones && p.phones.length > 0) ? p.phones : (p.phone ? [p.phone] : []);
    for (const raw of phones) {
      const phone = normalizePhone(raw || '');
      if (!phone) continue;
      if (!buckets[phone]) buckets[phone] = {};
      if (!buckets[phone][lastname]) buckets[phone][lastname] = [];
      buckets[phone][lastname].push(i);
    }
  }

  for (const phone in buckets) {
    for (const lastname in buckets[phone]) {
      const idxs = buckets[phone][lastname];
      for (let k = 1; k < idxs.length; k++) union(idxs[0], idxs[k]);
    }
  }

  const groups: { [root: number]: ParsedPassenger[] } = {};
  for (let i = 0; i < n; i++) {
    const r = find(i);
    if (!groups[r]) groups[r] = [];
    groups[r].push(passengers[i]);
  }

  return Object.keys(groups).map(rootKey => {
    const members = groups[parseInt(rootKey, 10)];
    let firstnames = '';
    let registrationNumbers = '';
    let count = 0;
    let address = '';
    let subscription = '';
    let phone = '';
    const phones: string[] = [];

    for (const m of members) {
      firstnames = mergeUniqueTokens(firstnames, m.firstnames);
      registrationNumbers = mergeUniqueTokens(registrationNumbers, m.registrationNumbers);
      count += m.count || 1;
      if (!address && m.address) address = m.address;
      if (!subscription && m.subscription) subscription = m.subscription;
      if (!phone && m.phone) phone = m.phone;
      for (const raw of (m.phones || (m.phone ? [m.phone] : []))) {
        const pn = normalizePhone(raw || '');
        if (pn && phones.indexOf(pn) === -1) phones.push(pn);
      }
    }

    return {
      lastname: members[0].lastname,
      firstnames: firstnames || '-',
      registrationNumbers: registrationNumbers || '-',
      phone: phone || (phones.length > 0 ? phones[0] : ''),
      phones: phones,
      address: address || '-',
      subscription: subscription || 'Aller/Retour',
      count: count
    };
  });
}

// ============================================================
// Registry of all supported SaaS sources
// ============================================================

export const SAAS_SOURCES: SaaSSource[] = [
  {
    id: 'koolskools',
    name: 'Koolskools',
    parse: parseKoolskools
  },
  {
    id: 'emadariss-without-tel',
    name: 'Emadariss sans téléphone',
    parse: parseEmadariss
  },
  {
    id: 'emadariss',
    name: 'Emadariss',
    parse: parseEmadarissAvecMatricule
  }
];



// WEBPACK FOOTER //
// ./src/app/components/passenger/passenger-sync/passenger-sync-parsers.ts